import { compareRates, type RateSample } from './agent-rate-stats';

import type { OnlineEvalAgentCountsResponse } from './online-eval.types';

export const DEFAULT_CHART_AGENT_COUNT = 4;
export const DEFAULT_CHART_CATEGORY_COUNT = 9;

// Counts in one unit, such as chats: how many the agent was evaluated on, and how
// many of those have a finding in each category
export type AgentTotal = { agent: string; count: number };
export type AgentCategoryTotal = {
  agent: string;
  category: string;
  count: number;
};

export type ChartAgent = {
  name: string;
  rawAgents: string[];
  // The denominator of every share
  total: number;
  countsByCategory: Record<string, number>;
};

export type CoalescedAgents = {
  agents: ChartAgent[];
  patternError: string | null;
};

const stripFromAgentName = (agent: string, pattern: RegExp | null): string => {
  if (pattern === null) return agent;
  const stripped = agent
    .replace(pattern, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
  return stripped === '' ? agent : stripped;
};

const compileStripPattern = (
  stripPattern: string
): { pattern: RegExp | null; error: string | null } => {
  if (stripPattern === '') return { pattern: null, error: null };
  try {
    return { pattern: new RegExp(stripPattern, 'g'), error: null };
  } catch (error) {
    return { pattern: null, error: (error as Error).message };
  }
};

// Agents whose names coincide after stripping merge into one bar with summed counts.
// Agents are ordered by total, so the first N are the most common. An agent with
// findings but no total has nothing to divide by and is dropped.
export const coalesceAgents = (
  totals: AgentTotal[],
  categoryTotals: AgentCategoryTotal[],
  stripPattern: string
): CoalescedAgents => {
  const { pattern, error } = compileStripPattern(stripPattern);
  const agentsByName = new Map<string, ChartAgent>();
  const agentFor = (rawAgent: string): ChartAgent => {
    const name = stripFromAgentName(rawAgent, pattern);
    const agent = agentsByName.get(name) ?? {
      name,
      rawAgents: [],
      total: 0,
      countsByCategory: {},
    };
    if (!agent.rawAgents.includes(rawAgent)) agent.rawAgents.push(rawAgent);
    agentsByName.set(name, agent);
    return agent;
  };
  for (const row of totals) {
    agentFor(row.agent).total += row.count;
  }
  for (const row of categoryTotals) {
    const agent = agentFor(row.agent);
    agent.countsByCategory[row.category] =
      (agent.countsByCategory[row.category] ?? 0) + row.count;
  }
  const agents = [...agentsByName.values()]
    .filter((agent) => agent.total > 0)
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  return { agents, patternError: error };
};

// An agent with no evaluated chats on record, because its findings predate their recording, has no chat count
export type FlaggedAgent = { agent: string; chatCount: number | null };

// The agents with a finding under the current filters, the most evaluated chats first.
// The counts ignore the agent filter, so selecting agents never hides the others.
export const rankFlaggedAgents = (
  counts: OnlineEvalAgentCountsResponse
): FlaggedAgent[] => {
  const chatCounts = new Map(
    counts.agent_counts.map((row) => [row.agent, row.chat_count])
  );
  return [...new Set(counts.agent_category_counts.map((row) => row.agent))]
    .map((agent) => ({ agent, chatCount: chatCounts.get(agent) ?? null }))
    .sort(
      (a, b) =>
        (b.chatCount ?? -1) - (a.chatCount ?? -1) ||
        a.agent.localeCompare(b.agent)
    );
};

export const shareOfFindings = (agent: ChartAgent, category: string): number =>
  agent.total === 0 ? 0 : (agent.countsByCategory[category] ?? 0) / agent.total;

export const sampleOf = (agent: ChartAgent, category: string): RateSample => ({
  k: agent.countsByCategory[category] ?? 0,
  n: agent.total,
});

// A category discriminates between agents when their shares of it differ most.
// Ties break toward the category with more findings, then by name.
export const rankCategoriesByDiscrimination = (
  agents: ChartAgent[]
): string[] => {
  const categories = new Set(
    agents.flatMap((agent) => Object.keys(agent.countsByCategory))
  );
  const scored = [...categories].map((category) => {
    const shares = agents.map((agent) => shareOfFindings(agent, category));
    const total = agents.reduce(
      (sum, agent) => sum + (agent.countsByCategory[category] ?? 0),
      0
    );
    return {
      category,
      spread: Math.max(...shares) - Math.min(...shares),
      total,
    };
  });
  return scored
    .sort(
      (a, b) =>
        b.spread - a.spread ||
        b.total - a.total ||
        a.category.localeCompare(b.category)
    )
    .map((row) => row.category);
};

// With exactly two agents the test between them is well defined, so categories
// rank by how confidently the two rates differ, not by the raw gap
export const rankCategoriesByComparison = (
  a: ChartAgent,
  b: ChartAgent
): string[] => {
  const categories = new Set([
    ...Object.keys(a.countsByCategory),
    ...Object.keys(b.countsByCategory),
  ]);
  return [...categories]
    .map((category) => ({
      category,
      comparison: compareRates(sampleOf(a, category), sampleOf(b, category)),
    }))
    .sort(
      (x, y) =>
        (x.comparison?.pValue ?? 1) - (y.comparison?.pValue ?? 1) ||
        Math.abs(y.comparison?.difference ?? 0) -
          Math.abs(x.comparison?.difference ?? 0) ||
        x.category.localeCompare(y.category)
    )
    .map((row) => row.category);
};
